import type { OsMASettings } from "../../model";
import { COLORS, GRID } from "features/chart";

const TOP = 8;
const BOTTOM = 20;

export function drawOsMA(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  values: (number | null)[],
  visibleStart: number,
  visibleEnd: number,
  settings: OsMASettings,
): void {
  const { left, right } = GRID;
  const chartW = width - left - right;
  const chartH = height - TOP - BOTTOM;

  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);

  const startIdx = Math.floor(visibleStart);
  const endIdx = Math.ceil(visibleEnd);
  const visibleCount = Math.floor(visibleEnd) - startIdx + 1;
  const cw = chartW / visibleCount;

  let absMax = 0;
  for (let i = startIdx; i <= endIdx; i++) {
    const v = values[i];
    if (v === null || v === undefined) continue;
    if (Math.abs(v) > absMax) absMax = Math.abs(v);
  }
  if (!absMax) absMax = 1;
  const pad = absMax * 0.1;
  const maxVal = absMax + pad;
  const range = maxVal * 2;
  const valToY = (v: number) => TOP + ((maxVal - v) / range) * chartH;
  const zeroY = valToY(0);

  ctx.font = "10px sans-serif";
  const lineEndX = width - right - ctx.measureText(maxVal.toFixed(5)).width - 6;

  ctx.strokeStyle = COLORS.grid;
  ctx.lineWidth = 1;
  for (let i = 0; i <= 2; i++) {
    const y = TOP + (chartH * i) / 2;
    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(lineEndX, y);
    ctx.stroke();
  }

  ctx.strokeStyle = "rgba(255,255,255,0.2)";
  ctx.beginPath();
  ctx.moveTo(left, zeroY);
  ctx.lineTo(lineEndX, zeroY);
  ctx.stroke();

  if (settings.histogramVisible) {
    const barW = Math.max(1, cw * 0.8);
    ctx.fillStyle = settings.histogramColor;
    for (let i = startIdx; i <= endIdx; i++) {
      const v = values[i];
      if (v === null || v === undefined) continue;
      const x = left + (i - startIdx + 0.5) * cw;
      const y = valToY(v);
      const h = Math.abs(y - zeroY) || 1;
      ctx.fillRect(x - barW / 2, Math.min(y, zeroY), barW, h);
    }
  }

  ctx.fillStyle = COLORS.label;
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";
  for (let i = 0; i <= 2; i++) {
    const v = maxVal - (range * i) / 2;
    const y = valToY(v);
    if (y < TOP || y > height - BOTTOM) continue;
    ctx.fillText(v === 0 ? "0" : v.toFixed(5), width - right + 2, y);
  }
}
